import React from 'react';
import { Tooltip } from '@mui/material';
import {
  getLinkedInConnectionStatusLabel,
  getLinkedInConnectionStatusTooltip,
  resolveLinkedInConnectionIconVariant,
} from '../../../utils/linkedInConnectionStatus';
import { ReactComponent as GridConnectedIcon } from '../../../assets/icons/applicant-search-grid/linkedin-connected.svg';
import { ReactComponent as GridPendingIcon } from '../../../assets/icons/applicant-search-grid/linkedin-pending.svg';
import { ReactComponent as GridFailedIcon } from '../../../assets/icons/applicant-search-grid/linkedin-invitation-failed.svg';
import { ReactComponent as GridNotSentIcon } from '../../../assets/icons/applicant-search-grid/linkedin-invitation-not-sent.svg';
import { ReactComponent as CardConnectedIcon } from '../../../assets/icons/applicant-card/linkedin-connected.svg';
import { ReactComponent as CardPendingIcon } from '../../../assets/icons/applicant-card/linkedin-pending.svg';
import { ReactComponent as CardFailedIcon } from '../../../assets/icons/applicant-card/linkedin-invitation-failed.svg';
import { ReactComponent as CardNotSentIcon } from '../../../assets/icons/applicant-card/linkedin-invitation-not-sent.svg';

type LinkedInConnectionStatusIconProps = {
  status: string | null | undefined;
  variant: 'grid' | 'card';
  className?: string;
  disableTooltip?: boolean;
};

const GRID_ICON_SIZE_PX = 21;
const CARD_ICON_SIZE_PX = 40;

const GRID_ICONS = {
  connected: GridConnectedIcon,
  pending: GridPendingIcon,
  failed: GridFailedIcon,
  'not-sent': GridNotSentIcon,
} as const;

const CARD_ICONS = {
  connected: CardConnectedIcon,
  pending: CardPendingIcon,
  failed: CardFailedIcon,
  'not-sent': CardNotSentIcon,
} as const;

const LinkedInConnectionStatusIcon: React.FC<LinkedInConnectionStatusIconProps> = ({
  status,
  variant,
  className,
  disableTooltip = false,
}) => {
  const iconVariant = resolveLinkedInConnectionIconVariant(status);
  if (!iconVariant) {
    return null;
  }

  const IconComponent = variant === 'grid' ? GRID_ICONS[iconVariant] : CARD_ICONS[iconVariant];
  const label = getLinkedInConnectionStatusLabel(status);
  const tooltip = getLinkedInConnectionStatusTooltip(status);

  const icon = (
    <IconComponent
      className={className}
      width={variant === 'grid' ? GRID_ICON_SIZE_PX : CARD_ICON_SIZE_PX}
      height={variant === 'grid' ? GRID_ICON_SIZE_PX : CARD_ICON_SIZE_PX}
      aria-hidden={label ? undefined : true}
      aria-label={label ?? undefined}
      role={label ? 'img' : undefined}
    />
  );

  const wrapperStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 0,
    ...(variant === 'card'
      ? { width: CARD_ICON_SIZE_PX, height: CARD_ICON_SIZE_PX, flexShrink: 0 }
      : {}),
  };

  if (disableTooltip || !tooltip) {
    return (
      <span style={wrapperStyle}>
        {icon}
      </span>
    );
  }

  return (
    <Tooltip title={tooltip} arrow>
      <span style={wrapperStyle}>
        {icon}
      </span>
    </Tooltip>
  );
};

export default LinkedInConnectionStatusIcon;
